import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  Button,
  CircularProgress,
} from "@mui/material";
import axios from "../../../config/axiosConfig";
import MuiAlert from "@mui/material/Alert";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";

// Estado 3 (Asignado) de una OT de Venta: espejo de indexad.php + cargaDetallead.php
// + guardaad.php — a diferencia de Reemplazo, acá no se busca en un catálogo grande:
// solo se activa lo que YA se vendió en esta misma OT (estado='Vendido').
const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const tableSx = { "& .MuiTableCell-root": { py: 0.1, px: 0.6, fontSize: 10.5, lineHeight: 1.3 } };

const FAMILIA_LABEL = { RADIO: "Radios", BATERIA: "Baterías", MODEM: "Módems", CHIP: "Chips" };

const ActivarVendidoOT = ({ cod_ot, cod_cliente, cod_servicio_cliente, esTKR, esAVL, otVehi, onEquiposActualizados }) => {
  const [activados, setActivados] = useState([]);
  const [vendidos, setVendidos] = useState([]);
  const [placasServicio, setPlacasServicio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [camposExtra, setCamposExtra] = useState({}); // { [key]: { grupo, placa, id_ref } }
  const [activando, setActivando] = useState(null);
  const [error, setError] = useState("");
  const [equipoDialog, setEquipoDialog] = useState(null);

  const cargar = useCallback(() => {
    setLoading(true);
    const peticiones = [
      axios.get(`/api/ot-venta/activados/${cod_ot}`),
      axios.get(`/api/ot-venta/vendidos/${cod_ot}`),
    ];
    if (esAVL && otVehi) peticiones.push(axios.get(`/api/ot-venta-placa/seleccionadas/${cod_ot}`));
    Promise.all(peticiones)
      .then(([resActivados, resVendidos, resPlacas]) => {
        setActivados(resActivados.data || []);
        setVendidos(resVendidos.data || []);
        if (resPlacas) setPlacasServicio(resPlacas.data || []);
      })
      .catch(() => { setActivados([]); setVendidos([]); })
      .finally(() => setLoading(false));
  }, [cod_ot, esAVL, otVehi]);

  useEffect(() => { cargar(); }, [cargar]);

  const setCampoExtra = (key, campo, valor) => {
    setCamposExtra((prev) => ({ ...prev, [key]: { ...prev[key], [campo]: valor } }));
  };

  const handleActivar = async (row) => {
    const key = `${row.familia}-${row.identificador}`;
    setActivando(key);
    setError("");
    try {
      const extra = camposExtra[key] || {};
      await axios.post("/api/ot-venta/activar", {
        cod_ot,
        familia: row.familia,
        identificador: row.identificador,
        cod_cliente,
        cod_servicio_cliente,
        grupo: extra.grupo || "",
        placa: extra.placa || "",
        id_ref: extra.id_ref || "",
      });
      cargar();
      if (onEquiposActualizados) onEquiposActualizados();
    } catch (err) {
      setError(err?.response?.data?.error || `No se pudo activar "${row.identificador}".`);
    } finally {
      setActivando(null);
    }
  };

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={22} /></Box>;
  }

  const grupos = esTKR
    ? [["RADIO", vendidos.filter((v) => v.familia === "RADIO")], ["BATERIA", vendidos.filter((v) => v.familia === "BATERIA")]]
    : [["MODEM", vendidos.filter((v) => v.familia === "MODEM")], ["CHIP", vendidos.filter((v) => v.familia === "CHIP")]];

  return (
    <Box sx={{ mt: 1 }}>
      {error && <MuiAlert severity="error" variant="outlined" sx={{ mb: 1, fontSize: 11, py: 0 }}>{error}</MuiAlert>}

      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Ya activado en esta OT</Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 200, borderRadius: 0.5 }}>
        <Table size="small" stickyHeader sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Identificador</TableCell>
              <TableCell sx={headCellSx}>Modelo</TableCell>
              <TableCell sx={headCellSx}>Placa</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!activados.length ? (
              <TableRow><TableCell colSpan={3}><Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>Todavía no se activó ningún equipo.</Typography></TableCell></TableRow>
            ) : activados.map((row) => (
              <TableRow key={`${row.familia}-${row.identificador}`} hover>
                <TableCell>
                  <Box component="span" onClick={() => setEquipoDialog({ tipo: row.familia.toLowerCase(), identificador: row.identificador })} sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}>
                    {row.identificador}
                  </Box>
                </TableCell>
                <TableCell>{row.modelo}</TableCell>
                <TableCell>{row.placa || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {grupos.map(([familia, rows]) => rows.length > 0 && (
        <Paper key={familia} variant="outlined" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, p: 1 }}>{FAMILIA_LABEL[familia]} vendidos para activar</Typography>
          <TableContainer>
            <Table size="small" sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Identificador</TableCell>
                  <TableCell sx={headCellSx}>Modelo</TableCell>
                  {familia === "RADIO" && (<><TableCell sx={headCellSx}>Grupo</TableCell><TableCell sx={headCellSx}>Placa</TableCell><TableCell sx={headCellSx}>ID Cliente</TableCell></>)}
                  {familia === "MODEM" && (<><TableCell sx={headCellSx}>Placa</TableCell><TableCell sx={headCellSx}>ID Cliente</TableCell></>)}
                  <TableCell sx={{ ...headCellSx, width: 90 }}>Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const key = `${row.familia}-${row.identificador}`;
                  const extra = camposExtra[key] || {};
                  return (
                    <TableRow key={key} hover>
                      <TableCell>{row.identificador}</TableCell>
                      <TableCell>{row.modelo}</TableCell>
                      {familia === "RADIO" && (
                        <>
                          <TableCell><TextField variant="standard" size="small" value={extra.grupo || ""} onChange={(e) => setCampoExtra(key, "grupo", e.target.value)} sx={{ width: 70, "& .MuiInputBase-input": { fontSize: 11 } }} /></TableCell>
                          <TableCell><TextField variant="standard" size="small" value={extra.placa || ""} onChange={(e) => setCampoExtra(key, "placa", e.target.value)} sx={{ width: 70, "& .MuiInputBase-input": { fontSize: 11 } }} /></TableCell>
                          <TableCell><TextField variant="standard" size="small" value={extra.id_ref || ""} onChange={(e) => setCampoExtra(key, "id_ref", e.target.value)} sx={{ width: 90, "& .MuiInputBase-input": { fontSize: 11 } }} /></TableCell>
                        </>
                      )}
                      {familia === "MODEM" && (
                        <>
                          <TableCell>
                            {otVehi ? (
                              <Select variant="standard" size="small" value={extra.placa || ""} onChange={(e) => setCampoExtra(key, "placa", e.target.value)} displayEmpty sx={{ fontSize: 11, minWidth: 100 }}>
                                <MenuItem value=""><em>Sin placa</em></MenuItem>
                                {placasServicio.map((p) => (<MenuItem key={p.placa} value={p.placa} sx={{ fontSize: 11 }}>{p.placa}</MenuItem>))}
                              </Select>
                            ) : (
                              <TextField variant="standard" size="small" value={extra.placa || ""} onChange={(e) => setCampoExtra(key, "placa", e.target.value)} sx={{ width: 90, "& .MuiInputBase-input": { fontSize: 11 } }} />
                            )}
                          </TableCell>
                          <TableCell><TextField variant="standard" size="small" value={extra.id_ref || ""} onChange={(e) => setCampoExtra(key, "id_ref", e.target.value)} sx={{ width: 90, "& .MuiInputBase-input": { fontSize: 11 } }} /></TableCell>
                        </>
                      )}
                      <TableCell>
                        <Button size="small" variant="contained" color="success" disabled={activando === key} onClick={() => handleActivar(row)} sx={{ minHeight: 22, height: 22, fontSize: 10.5 }}>
                          {activando === key ? <CircularProgress size={14} color="inherit" /> : "Activar"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ))}

      {!vendidos.length && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
          No hay equipos vendidos pendientes de activar.
        </Typography>
      )}

      <CentroEquipoDetalleDialog
        open={!!equipoDialog}
        tipo={equipoDialog?.tipo}
        identificador={equipoDialog?.identificador}
        onClose={() => setEquipoDialog(null)}
      />
    </Box>
  );
};

export default ActivarVendidoOT;
